// src/pages/AdminReport.jsx
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../utils/api";
import { useAuth } from "../context/AuthContext";
import { useDarkMode, DM } from "../context/DarkModeContext";
import DashboardHeader from "../components/DashboardHeader";
import { loadAllSections } from "../utils/sectionUtils";
import { SECTIONS, SECTION_FIELDS } from "../constants/sections";
import DentalNewsTicker from "../components/DentalNewsTicker";
import { useReactToPrint } from "react-to-print";
import { downloadPDF, formatDuration, fetchDoctorFilter } from "../utils/reportUtils";

const REPORT_COLUMNS = {
  articlePublications: ["Si No","Title of Article","Journal Details","Author Name and Position","DCI Category and Impact Factor","Acceptance Details","Month and Year of Publication","Points","Current Status","Link for Article"],
  textBookPublications: ["Si No","Title of Text Book","Publisher Details","Author Name","DCI Category and Impact Factor","Acceptance Details","Month and Year of Publication","Points","Current Status","Link for Text Book"],
  patents: ["Si No","Title of Patent","Application Submission Details","Author Name","Acceptance Details","Month and Year of Publication","Points","Current Status","Link for Patent"],
  copyrights: ["Si No","Title","Application Submission Details","Author Name","Acceptance Details","Month and Year of Publication","Points","Current Status","Link for Copyright"],
  fellowships: ["Si No","Title","Fellowship Registration Details","Faculty Name","Month and Year of Initiation","Month and Year of Completion","Points","Current Status","Link for Fellowship"],
  phd: ["Si No","Title","PhD Registration Details","Faculty Name","Month and Year of Initiation","Month and Year of Completion","Points","Current Status","Link for PhD"],
  swayamCourses: ["Si No","Title","Swayam Registration Details","Faculty Name","Month and Year of Initiation","Month and Year of Completion","Points","Current Status","Link for Course"],
  awards: ["Si No","Award Name","Award Given By","Faculty Name","Month and Date","Current Status","Link for Award"],
  innovativeTeaching: ["Si No","Faculty Name","Month and Date","Topic","Innovation Details","Theory or Practical Session","Link for Session"],
  eContent: ["Si No","Faculty Name","Month and Date","Topic","E-Content Details","Theory or Practical Session","Link for Content"],
  researchGrants: ["Si No","Faculty Name","Month and Date","Topic","Grant Details","Funding Agency Details","Link for Grant"],
  guestLectures: ["Si No","Faculty Name","Month and Date","Topic","Guest Lecture Details","Organized By","Link Details"],
  conferencePresentation: ["Si No","Faculty Name","Month and Date","Topic","Presentation Details","Organized By","Link"],
  shortResearchProjects: ["Si No","Faculty Name","Month and Date","Topic","Proposal Details","Investigator Details","Link Details"],
};

const COLUMN_KEYS = {
  articlePublications: ["si_no","title_of_article","journal_details","author_name_and_position","dci_category_and_impact_factor","acceptance_details","month_and_year_of_publication","points","current_status","link_for_article"],
  textBookPublications: ["si_no","title_of_text_book","publisher_details","author_name","dci_category_and_impact_factor","acceptance_details","month_and_year_of_publication","points","current_status","link_for_text_book"],
  patents: ["si_no","title_of_patent","application_submission_details","author_name","acceptance_details","month_and_year_of_publication","points","current_status","link_for_patent"],
  copyrights: ["si_no","title","application_submission_details","author_name","acceptance_details","month_and_year_of_publication","points","current_status","link_for_copyright"],
  fellowships: ["si_no","title","fellowship_registration_details","faculty_name","month_and_year_of_initiation","month_and_year_of_completion","points","current_status","link_for_fellowship"],
  phd: ["si_no","title","phd_registration_details","faculty_name","month_and_year_of_initiation","month_and_year_of_completion","points","current_status","link_for_phd"],
  swayamCourses: ["si_no","title","swayam_registration_details","faculty_name","month_and_year_of_initiation","month_and_year_of_completion","points","current_status","link_for_course"],
  awards: ["si_no","award_name","award_given_by","faculty_name","month_and_date","current_status","link_for_award"],
  innovativeTeaching: ["si_no","faculty_name","month_and_date","topic","innovation_details","session_type","link_for_session"],
  eContent: ["si_no","faculty_name","month_and_date","topic","e_content_details","session_type","link_for_content"],
  researchGrants: ["si_no","faculty_name","month_and_date","topic","grant_details","funding_agency_details","link_for_grant"],
  guestLectures: ["si_no","faculty_name","month_and_date","topic","guest_lecture_details","organized_by","link_details"],
  conferencePresentation: ["si_no","faculty_name","month_and_date","topic","presentation_details","organized_by","link"],
  shortResearchProjects: ["si_no","faculty_name","month_and_date","topic","proposal_details","investigator_details","link_details"],
};

export default function AdminReport() {
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const printRef = useRef();

  const [facultyList, setFacultyList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState("articlePublications");
  const [showSubmitted, setShowSubmitted] = useState(false);
  const [submittedReports, setSubmittedReports] = useState([]);
  const [filterDept, setFilterDept] = useState("");
  const [filterEmpId, setFilterEmpId] = useState("");
  const [filterName, setFilterName] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterSubmission, setFilterSubmission] = useState(""); // "submitted" | "not_submitted" | ""
  const [departments, setDepartments] = useState([]);
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [filterSubmittedName, setFilterSubmittedName] = useState("");
  
  const [allSections, setAllSections] = useState(SECTIONS);
  const [dynamicCols, setDynamicCols] = useState([]);
  const [dynamicKeys, setDynamicKeys] = useState([]);
  const [selectedFields, setSelectedFields] = useState({}); // Key: key, Value: boolean
  const [showFieldSelector, setShowFieldSelector] = useState(false);

  useEffect(() => { 
    fetchData(); 
    fetchSubmittedReports(); 
    loadAllSections().then(setAllSections);
  }, []);

  useEffect(() => {
    async function fetchDynamicFields() {
      let cols = [];
      let keys = [];
      // Always attempt to fetch from Firestore first for any section (built-in or custom)
      try {
        const res = await api.get(`/section_fields_${activeSection}?orderBy=order`);
        const fieldRows = Array.isArray(res)
          ? res.map((row) => ({
              ...(typeof row.data === "string" ? JSON.parse(row.data) : row.data || row),
            }))
          : [];
        if (fieldRows.length > 0) {
          const fields = fieldRows;
          // Standard first columns for reports
          cols = ["Si No", "Faculty Name", "Month & Year", "Current Status"];
          keys = ["si_no", "faculty_name", "unified_date", "current_status"];
          
          fields.forEach(f => {
            const k = f.key || f.name?.toLowerCase().replace(/[^a-z0-9]/g, "_");
            if (!["month_and_year_of_publication", "month_and_date", "month_and_year_of_initiation", "date", "month_and_year"].includes(k)) {
              cols.push(f.label || f.name);
              keys.push(k);
            }
          });
          
          setDynamicCols(cols);
          setDynamicKeys(keys);
        } else if (REPORT_COLUMNS[activeSection]) {
          // If Firestore is empty, fall back to hardcoded columns for built-in sections
          cols = REPORT_COLUMNS[activeSection];
          keys = COLUMN_KEYS[activeSection];
          setDynamicCols(cols);
          setDynamicKeys(keys);
        } else {
          // Fallback for custom sections with no fields defined yet
          cols = ["Si No", "Faculty Name", "Current Status"];
          keys = ["si_no", "faculty_name", "current_status"];
          setDynamicCols(cols);
          setDynamicKeys(keys);
        }
      } catch (err) {
        console.error("Failed to fetch fields for report:", err);
        // Fallback on error
        if (REPORT_COLUMNS[activeSection]) {
          cols = REPORT_COLUMNS[activeSection];
          keys = COLUMN_KEYS[activeSection];
        } else {
          cols = ["Si No", "Faculty Name", "Current Status"];
          keys = ["si_no", "faculty_name", "current_status"];
        }
        setDynamicCols(cols);
        setDynamicKeys(keys);
      }
      
      // Initialize selected fields (all selected by default)
      const initial = {};
      keys.forEach(k => { initial[k] = true; });
      setSelectedFields(initial);
    }
    fetchDynamicFields();
  }, [activeSection]);

  async function fetchData() {
    setLoading(true);
    try {
      const [usersRes, isDoctorUser] = await Promise.all([
        api.get("/users"),
        fetchDoctorFilter(),
      ]);

      const list = (Array.isArray(usersRes) ? usersRes : [])
        .map((row) => {
          const profileData = typeof row.data === "string" ? JSON.parse(row.data) : row.data || row;
          const facultyId = row.id || profileData?.id || profileData?.uid || "";
          const department = (profileData?.department || profileData?.departmentName || "").toString().trim();
          return {
            id: facultyId,
            data: profileData,
            ...profileData,
            department,
            departmentName: department,
            name: profileData?.name || profileData?.displayName || row?.name || row?.displayName || "",
          };
        })
        .filter(u =>
          (u.role === "faculty" || u.role === "admin") &&
          isDoctorUser(u)
        )
        .sort((a, b) => (a.name || "").localeCompare(b.name || ""));

      console.log("[AdminReport] fetched faculty IDs", list.map((faculty) => ({ id: faculty.id, type: typeof faculty.id })));
      const sectionData = await Promise.all(
        list.map(async (faculty) => {
          const facultyId = faculty.id || faculty.data?.id || faculty.data?.uid;
          if (!facultyId) {
            console.warn("AdminReport: skipping faculty without an ID", faculty);
            return { facultyId: null, entries: {} };
          }
          const sectionEntries = await Promise.all(
            allSections.map(async (section) => {
              try {
                const candidateIds = [String(facultyId), String(facultyId).trim(), Number(facultyId)].filter((value, index, arr) => value !== "" && value !== undefined && value !== null && arr.indexOf(value) === index);
                let lastError;
                for (const candidateId of candidateIds) {
                  try {
                    const res = await api.get(`/faculty/${candidateId}/section/${section.key}`);
                    if (Array.isArray(res)) {
                      return [section.key, res];
                    }
                  } catch (err) {
                    lastError = err;
                  }
                }
                if (lastError) console.warn(`AdminReport: failed to fetch ${section.key} for faculty ${facultyId}`, lastError);
                return [section.key, []];
              } catch (err) {
                console.warn(`AdminReport: failed to fetch ${section.key} for faculty ${facultyId}`, err);
                return [section.key, []];
              }
            })
          );
          return { facultyId, entries: Object.fromEntries(sectionEntries) };
        })
      );

      const byFaculty = Object.fromEntries(sectionData.filter(item => item.facultyId).map((item) => [item.facultyId, item.entries]));
      const enrichedFaculty = list.map((faculty) => ({
        ...faculty,
        ...byFaculty[faculty.id],
      }));

      const missingProfileRows = enrichedFaculty.filter((faculty) => !faculty.name && !faculty.data?.name && !faculty.data?.displayName);
      if (missingProfileRows.length > 0) {
        console.warn("AdminReport: some faculty rows have missing profile names", missingProfileRows.map((faculty) => faculty.id));
      }

      setFacultyList(enrichedFaculty);
      const depts = [...new Set(enrichedFaculty.map(f => (f.data?.department || f.data?.departmentName || f.department || f.departmentName || "").toString().trim()).filter(Boolean))].sort();
      setDepartments(depts);
    } catch (err) { console.error(err); }
    setLoading(false);
  }

  async function fetchSubmittedReports() {
    try {
      const res = await api.get("/submitted_reports");
      const reports = (Array.isArray(res) ? res : []).map((row) => ({
        id: row.id,
        ...(typeof row.data === "string" ? JSON.parse(row.data) : row.data || row),
      }));
      reports.sort(
        (a, b) => new Date(b.submittedAt || 0).getTime() - new Date(a.submittedAt || 0).getTime()
      );
      setSubmittedReports(reports);
    } catch {}
  }

  async function handleDeleteSubmission(id) {
    if (!window.confirm("Are you sure you want to delete this submission record? This will not delete the entries themselves, only the record of this report submission.")) return;
    try {
      await api.delete(`/submitted_reports/${id}`);
      setSubmittedReports(prev => prev.filter(r => r.id !== id));
      alert("✅ Submission record deleted successfully!");
    } catch (err) {
      console.error(err);
      alert("❌ Failed to delete submission record.");
    }
  }

  async function handleLogout() { await logout(); navigate("/admin"); }

  const getNameWithTitle = (faculty) => {
    if (!faculty) return "—";
    const title = faculty.title || faculty.salutation || faculty.prefix || "";
    if (!title) return faculty.name || "—";
    return `${title} ${faculty.name}`.trim();
  };

  function getEntryValue(entry, key) {
    if (key === "si_no") return "";
    if (key === "unified_date") {
      return entry.monthAndYearOfPublication || entry.month_and_year_of_publication || entry.monthAndDate || entry.month_and_date || entry.monthAndYearOfInitiation || entry.month_and_year_of_initiation || entry.date || entry.monthAndYear || entry.month_and_year || "—";
    }
    let val = entry[key];
    if (val === undefined) {
      const camel = key.replace(/_([a-z])/g, (_, l) => l.toUpperCase());
      val = entry[camel];
    }
    if (val === undefined) return "—";
    
    // Check if it's an experience field to apply formatting
    const keyLower = key.toLowerCase();
    if (keyLower.includes("exp") || keyLower.includes("duration")) {
      return formatDuration(val);
    }
    
    return val || "—";
  }

  function getReportRows() {
    const rows = [];
    let siNo = 1;
    for (const faculty of facultyList) {
      if (faculty.status === "inactive") continue;
      const facultyDepartment = (faculty.data?.department || faculty.data?.departmentName || faculty.department || faculty.departmentName || "").toString().trim();
      if (filterDept && facultyDepartment !== filterDept) continue;
      if (filterEmpId && !faculty.empId?.toLowerCase().includes(filterEmpId.toLowerCase())) continue;

      if (filterName && !faculty.name?.toLowerCase().includes(filterName.toLowerCase())) continue;
      // Submission filter
      const hasSubmitted = submittedReports.some(r => r.facultyId === faculty.id && r.sectionKey === activeSection);
      if (filterSubmission === "submitted" && !hasSubmitted) continue;
      if (filterSubmission === "not_submitted" && hasSubmitted) continue;
      const entries = faculty[activeSection] || [];
      for (const entry of entries) {
        if (filterStatus && getEntryValue(entry, "current_status") !== filterStatus) continue;
        
        // Date range filter for main report
        if (filterDateFrom || filterDateTo) {
          let dateVal = entry.monthAndYearOfPublication || entry.month_and_year_of_publication || entry.monthAndDate || entry.month_and_date || entry.monthAndYearOfInitiation || entry.month_and_year_of_initiation || entry.date || entry.monthAndYear || entry.month_and_year || "";
          if (!dateVal) continue;
          
          let entryDateStr = "";
          const entryDate = new Date(dateVal);
          if (!isNaN(entryDate)) {
             const yy = entryDate.getFullYear();
             const mm = String(entryDate.getMonth() + 1).padStart(2, "0");
             const dd = String(entryDate.getDate()).padStart(2, "0");
             entryDateStr = `${yy}-${mm}-${dd}`;
          } else {
             entryDateStr = dateVal.toString().trim();
          }

          if (filterDateFrom && entryDateStr < filterDateFrom) continue;
          if (filterDateTo && entryDateStr > filterDateTo) continue;
        }

        rows.push({ siNo: siNo++, faculty, entry });
      }
    }
    return rows;
  }



  function getFilteredSubmitted() {
    const sectionReports = submittedReports.filter(r => r.sectionKey === activeSection);
    return sectionReports.filter(r => {
      if (filterSubmittedName && !r.facultyName?.toLowerCase().includes(filterSubmittedName.toLowerCase())) return false;
      if (filterDateFrom || filterDateTo) {
        const dt = r.submittedAt?.toDate ? r.submittedAt.toDate() : null;
        if (!dt) return false;
        const dateStr = dt.toISOString().split("T")[0];
        if (filterDateFrom && dateStr < filterDateFrom) return false;
        if (filterDateTo && dateStr > filterDateTo) return false;
      }
      return true;
    });
  }

  function handleExportSubmittedExcel() {
    const rows = getFilteredSubmitted();
    const headers = ["Si No", "Faculty Name", "Emp ID", "Department", "Section", "Entries", "Status Filter", "Submitted Date"];
    let csv = headers.join(",") + "\n";
    rows.forEach((r, i) => {
      const dt = r.submittedAt?.toDate ? new Date(r.submittedAt.toDate()).toLocaleString() : "—";
      const row = [
        i + 1,
        `"${(r.facultyName || "").replace(/"/g, '""')}"`,
        `"${(r.facultyEmpId || "—").replace(/"/g, '""')}"`,
        `"${(r.facultyDept || "—").replace(/"/g, '""')}"`,
        `"${(r.sectionLabel || "").replace(/"/g, '""')}"`,
        r.totalEntries || 0,
        `"${(r.filterStatus || "—").replace(/"/g, '""')}"`,
        `"${dt}"`
      ];
      csv += row.join(",") + "\n";
    });
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Submitted_Reports_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function handlePrintSubmitted() {
    const rows = getFilteredSubmitted();
    const dateRange = filterDateFrom || filterDateTo
      ? ` | Date: ${filterDateFrom || "—"} to ${filterDateTo || "—"}`
      : "";
    const nameFilter = filterSubmittedName ? ` | Faculty: ${filterSubmittedName}` : "";
    const win = window.open("", "_blank");
    win.document.write(`
      <html><head><title>Submitted Reports</title>
      <style>
        body { font-family: Georgia, serif; padding: 20px; color: #1e293b; }
        h2 { color: #0f2557; margin-bottom: 4px; }
        p { color: #64748b; font-size: 12px; margin-bottom: 16px; }
        table { width: 100%; border-collapse: collapse; font-size: 12px; }
        th { background: #f5f3ff; color: #7c3aed; padding: 8px 10px; text-align: left; border-bottom: 2px solid #e2e8f0; }
        td { padding: 8px 10px; border-bottom: 1px solid #f1f5f9; }
        tr:nth-child(even) td { background: #fafafa; }
        .badge { background: #f5f3ff; color: #7c3aed; border-radius: 20px; padding: 2px 8px; font-size: 11px; font-weight: 700; }
      </style></head><body>
      <h2>📤 Submitted Reports</h2>
      <p>Adhiparasakthi Dental College${dateRange}${nameFilter} &nbsp;·&nbsp; Total: ${rows.length} records &nbsp;·&nbsp; Printed: ${new Date().toLocaleString()}</p>
      <table>
        <thead><tr><th>Si No</th><th>Faculty Name</th><th>Emp ID</th><th>Department</th><th>Section</th><th>Entries</th><th>Status Filter</th><th>Submitted Date</th></tr></thead>
        <tbody>
          ${rows.map((r, i) => {
            const dt = r.submittedAt?.toDate ? new Date(r.submittedAt.toDate()).toLocaleString("en-IN", { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) : "—";
            return `<tr>
              <td>${i + 1}</td>
              <td><b>${r.facultyName || "—"}</b></td>
              <td>${r.facultyEmpId || "—"}</td>
              <td>${r.facultyDept || "—"}</td>
              <td><span class='badge'>${r.sectionLabel || "—"}</span></td>
              <td><b>${r.totalEntries || 0}</b></td>
              <td>${r.filterStatus || "—"}</td>
              <td>${dt}</td>
            </tr>`;
          }).join("")}
        </tbody>
      </table>
      </body></html>
    `);
    win.document.close();
    win.print();
  }

  function handleExportCSV() {
    const rows = getReportRows();
    const visibleKeys = dynamicKeys.filter(k => selectedFields[k]);
    const visibleCols = dynamicCols.filter((_, i) => selectedFields[dynamicKeys[i]]);
    
    const sec = allSections.find(s => s.key === activeSection);
    
    let csv = visibleCols.join(",") + "\n";
    rows.forEach(({ siNo, faculty, entry }) => {
      const row = visibleKeys.map(key => {
        if (key === "si_no") return siNo;
        if (key === "faculty_name") return `"${(faculty.name || "").replace(/"/g, '""')}"`;
        const val = getEntryValue(entry, key);
        return `"${String(val).replace(/"/g, '""')}"`;
      });
      csv += row.join(",") + "\n";
    });
    
    // Add BOM for Excel compatibility
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${sec?.label || activeSection}_Consolidated_Report.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const activeSec = allSections.find(s => s.key === activeSection);
  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `${activeSec?.label || activeSection}_Consolidated_Report`,
  });

  const handleDownloadPDF = async () => {
    setLoading(true);
    await downloadPDF(printRef, `${activeSec?.label || activeSection}_Consolidated_Report.pdf`, { orientation: "l" });
    setLoading(false);
  };

  const reportRows = getReportRows();
  const cols = dynamicCols;
  const keys = dynamicKeys;
  const statusOptions = ["Sent","Accepted","Published","Initiated","In Progress","Completed","Received","Nominated","Pending"];

  return (
    <div style={{ minHeight: "100vh", background: "#f0f4ff", fontFamily: "Georgia, serif" }}>

      <DashboardHeader label="Admin">
        <button onClick={() => navigate("/admin/dashboard")} style={{ background: "#fff", color: "#1d4ed8", border: "none", borderRadius: 10, padding: "8px 18px", fontWeight: 800, fontSize: 16, cursor: "pointer" }}>← Dashboard</button>
        <button onClick={handleLogout} style={{ background: "rgba(255,255,255,0.15)", color: "#fff", border: "1px solid rgba(255,255,255,0.3)", borderRadius: 10, padding: "8px 16px", fontWeight: 600, fontSize: 15, cursor: "pointer" }}>🚪 Logout</button>
      </DashboardHeader>

      <div style={{ display: "flex", minHeight: "calc(100vh - 64px)" }}>

        {/* Sidebar */}
        <aside style={{ width: 230, background: "#fff", boxShadow: "2px 0 16px rgba(15,37,87,0.07)", paddingTop: 16, flexShrink: 0, overflowY: "auto" }}>
          <div style={{ padding: "8px 16px 12px", fontSize: 13, fontWeight: 700, color: "#94a3b8", letterSpacing: 1 }}>REPORT SECTIONS</div>
          {allSections.map(sec => {
            const total = facultyList.reduce((s, f) => s + (f[sec.key]?.length || 0), 0);
            return (
              <div key={sec.key} onClick={() => { setActiveSection(sec.key); setFilterStatus(""); setFilterSubmission(""); setShowSubmitted(false); }}
                style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 16px", cursor: "pointer", borderLeft: activeSection === sec.key && !showSubmitted ? `3px solid ${sec.color}` : "3px solid transparent", background: activeSection === sec.key && !showSubmitted ? sec.light : "transparent", color: activeSection === sec.key && !showSubmitted ? sec.color : "#475569", fontWeight: activeSection === sec.key && !showSubmitted ? 700 : 400, fontSize: 15, transition: "all 0.15s" }}>
                <span style={{ fontSize: 17 }}>{sec.icon}</span>
                <span style={{ flex: 1, lineHeight: 1.3 }}>{sec.label}</span>
                <span style={{ background: activeSection === sec.key && !showSubmitted ? sec.color : "#e2e8f0", color: activeSection === sec.key && !showSubmitted ? "#fff" : "#64748b", borderRadius: 20, padding: "1px 7px", fontSize: 13, fontWeight: 700 }}>{total}</span>
              </div>
            );
          })}
        </aside>

        {/* Main */}
        <main style={{ flex: 1, padding: "24px", overflowX: "auto" }}>

          {/* Title + Actions */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
            <div>
              <h1 style={{ fontSize: 24, fontWeight: 900, color: "#0f2557", margin: 0 }}>
                {showSubmitted ? `📤 Submitted ${activeSec?.label} Reports` : `${activeSec?.icon} ${activeSec?.label} — Report`}
              </h1>
              <p style={{ color: "#64748b", fontSize: 15, marginTop: 4 }}>
                {showSubmitted ? `${submittedReports.filter(r => r.sectionKey === activeSection).length} submissions from faculty` : `${reportRows.length} entries found`}
              </p>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => setShowSubmitted(v => !v)}
                style={{ background: showSubmitted ? "#7c3aed" : "#f5f3ff", color: showSubmitted ? "#fff" : "#7c3aed", border: "1px solid #7c3aed", borderRadius: 10, padding: "9px 18px", fontWeight: 700, cursor: "pointer", fontSize: 15 }}>
                📤 {showSubmitted ? "← Back to Report" : `Submitted Reports (${submittedReports.filter(r => r.sectionKey === activeSection).length})`}
              </button>
              {showSubmitted && (
                <>
                  <button onClick={handleExportSubmittedExcel} style={{ background: "#16a34a", color: "#fff", border: "none", borderRadius: 10, padding: "9px 20px", fontWeight: 700, cursor: "pointer", fontSize: 15 }}>
                    📊 Export Excel
                  </button>
                  <button onClick={handlePrintSubmitted} style={{ background: "#2563eb", color: "#fff", border: "none", borderRadius: 10, padding: "9px 20px", fontWeight: 700, cursor: "pointer", fontSize: 15 }}>
                    🖨️ Print
                  </button>
                </>
              )}
              {!showSubmitted && (
                <>
                  <div style={{ position: "relative" }}>
                    <button onClick={() => setShowFieldSelector(!showFieldSelector)}
                      style={{ background: "#fff", color: "#4b5563", border: "1.5px solid #d1d5db", borderRadius: 10, padding: "9px 18px", fontWeight: 700, cursor: "pointer", fontSize: 15, display: "flex", alignItems: "center", gap: 6 }}>
                      ⚙️ Select Fields
                    </button>
                    {showFieldSelector && (
                      <div style={{ position: "absolute", top: "100%", right: 0, marginTop: 8, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, boxShadow: "0 10px 25px rgba(0,0,0,0.15)", padding: 16, zIndex: 200, minWidth: 240, maxHeight: 400, overflowY: "auto" }}>
                        <div style={{ fontSize: 13, fontWeight: 800, color: "#94a3b8", marginBottom: 12, textTransform: "uppercase", letterSpacing: 0.5 }}>Visible Columns</div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                          {dynamicKeys.map((key, i) => (
                            <label key={key} style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", fontSize: 15, color: "#374151", fontWeight: 600 }}>
                              <input 
                                type="checkbox" 
                                checked={!!selectedFields[key]} 
                                onChange={() => setSelectedFields(prev => ({ ...prev, [key]: !prev[key] }))}
                                style={{ width: 16, height: 16, accentColor: "#2563eb" }}
                              />
                              {dynamicCols[i]}
                            </label>
                          ))}
                        </div>
                        <div style={{ borderTop: "1px solid #f1f5f9", marginTop: 12, paddingTop: 12, display: "flex", gap: 8 }}>
                          <button onClick={() => { const next = {}; dynamicKeys.forEach(k => next[k] = true); setSelectedFields(next); }} style={{ flex: 1, background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 6, padding: "6px", fontSize: 13, fontWeight: 700, color: "#64748b", cursor: "pointer" }}>Select All</button>
                          <button onClick={() => setShowFieldSelector(false)} style={{ flex: 1, background: "#2563eb", border: "none", borderRadius: 6, padding: "6px", fontSize: 13, fontWeight: 700, color: "#fff", cursor: "pointer" }}>Done</button>
                        </div>
                      </div>
                    )}
                  </div>
                  <button onClick={handleExportCSV} style={{ background: "#16a34a", color: "#fff", border: "none", borderRadius: 10, padding: "9px 20px", fontWeight: 700, cursor: "pointer", fontSize: 15 }}>
                    📊 Export Excel
                  </button>
                  <button onClick={handlePrint} style={{ background: "#2563eb", color: "#fff", border: "none", borderRadius: 10, padding: "9px 20px", fontWeight: 700, cursor: "pointer", fontSize: 15 }}>
                    🖨️ Print
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Submitted Reports View */}
          {showSubmitted ? (
            <>
              {/* Submitted Filters */}
              <div style={{ background: "#fff", borderRadius: 16, padding: "16px 20px", boxShadow: "0 2px 10px rgba(15,37,87,0.07)", marginBottom: 16, display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14 }}>
                <div>
                  <label style={{ fontSize: 13, fontWeight: 700, color: "#374151", display: "block", marginBottom: 5 }}>👤 Faculty Name</label>
                  <input value={filterSubmittedName} onChange={e => setFilterSubmittedName(e.target.value)} placeholder="Search name..."
                    style={{ width: "100%", background: "#f8fafc", border: "1.5px solid #e2e8f0", borderRadius: 8, padding: "8px 12px", fontSize: 15, outline: "none", boxSizing: "border-box" }} />
                </div>
                <div>
                  <label style={{ fontSize: 13, fontWeight: 700, color: "#374151", display: "block", marginBottom: 5 }}>📅 From Date</label>
                  <input type="date" value={filterDateFrom} onChange={e => setFilterDateFrom(e.target.value)}
                    style={{ width: "100%", background: "#f8fafc", border: "1.5px solid #e2e8f0", borderRadius: 8, padding: "8px 12px", fontSize: 15, outline: "none", boxSizing: "border-box", colorScheme: "light" }} />
                </div>
                <div>
                  <label style={{ fontSize: 13, fontWeight: 700, color: "#374151", display: "block", marginBottom: 5 }}>📅 To Date</label>
                  <input type="date" value={filterDateTo} onChange={e => setFilterDateTo(e.target.value)}
                    style={{ width: "100%", background: "#f8fafc", border: "1.5px solid #e2e8f0", borderRadius: 8, padding: "8px 12px", fontSize: 15, outline: "none", boxSizing: "border-box", colorScheme: "light" }} />
                </div>
                <div style={{ display: "flex", alignItems: "flex-end" }}>
                  <button onClick={() => { setFilterSubmittedName(""); setFilterDateFrom(""); setFilterDateTo(""); }}
                    style={{ width: "100%", background: "#f1f5f9", color: "#475569", border: "1.5px solid #e2e8f0", borderRadius: 8, padding: "8px 12px", fontSize: 15, fontWeight: 700, cursor: "pointer" }}>
                    🔄 Clear Filters
                  </button>
                </div>
              </div>

              <div style={{ background: "#fff", borderRadius: 16, boxShadow: "0 2px 10px rgba(15,37,87,0.07)", overflow: "hidden" }}>
                {(() => {
                  const filtered = getFilteredSubmitted();
                  const sectionReportCount = submittedReports.filter(r => r.sectionKey === activeSection).length;
                  return filtered.length === 0 ? (
                    <div style={{ padding: 60, textAlign: "center", color: "#94a3b8" }}>
                      <div style={{ fontSize: 42, marginBottom: 12 }}>📤</div>
                      <p>{sectionReportCount === 0 ? `No ${activeSec?.label} reports submitted by faculty yet.` : "No records match the selected filters."}</p>
                    </div>
                  ) : (
                    <>
                      <div style={{ padding: "12px 20px", borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", gap: 10 }}>
                        <span style={{ background: "#f5f3ff", color: "#7c3aed", borderRadius: 20, padding: "3px 12px", fontSize: 14, fontWeight: 700 }}>{filtered.length} records</span>
                        {(filterDateFrom || filterDateTo) && (
                          <span style={{ background: "#eaf1ff", color: "#2563eb", borderRadius: 20, padding: "3px 12px", fontSize: 13, fontWeight: 600 }}>
                            📅 {filterDateFrom || "—"} → {filterDateTo || "—"}
                          </span>
                        )}
                      </div>
                      <div style={{ overflowX: "auto" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 15 }}>
                          <thead>
                            <tr style={{ background: "#f5f3ff" }}>
                              {["Si No","Faculty Name","Emp ID","Department","Section","Entries","Status Filter","Submitted Date", "Actions"].map(h => (
                                <th key={h} style={{ padding: "12px 14px", textAlign: "left", fontSize: 13, fontWeight: 700, color: "#7c3aed", borderBottom: "2px solid #e2e8f0", whiteSpace: "nowrap", textTransform: "uppercase", letterSpacing: "0.5px" }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {filtered.map((r, i) => (
                              <tr key={i} style={{ borderBottom: "1px solid #f1f5f9", transition: "all 0.2s" }}
                                onMouseEnter={e => e.currentTarget.style.background = "#faf9ff"}
                                onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                                <td style={{ padding: "12px 14px", color: "#64748b", fontWeight: 600 }}>{i + 1}</td>
                                <td style={{ padding: "12px 14px" }}>
                                  <div style={{ fontWeight: 800, color: "#1e293b", fontSize: 16 }}>{r.facultyName}</div>
                                  <div style={{ fontSize: 13, color: "#94a3b8", marginTop: 2 }}>Submitted Report</div>
                                </td>
                                <td style={{ padding: "12px 14px", color: "#475569", fontWeight: 500 }}>{r.facultyEmpId || "—"}</td>
                                <td style={{ padding: "12px 14px", color: "#475569", fontSize: 14 }}>{r.facultyDept || "—"}</td>
                                <td style={{ padding: "12px 14px" }}>
                                  <span style={{ background: "#f5f3ff", color: "#7c3aed", borderRadius: 8, padding: "4px 10px", fontSize: 13, fontWeight: 800, border: "1px solid #ddd6fe" }}>{r.sectionLabel}</span>
                                </td>
                                <td style={{ padding: "12px 14px" }}>
                                  <span style={{ background: "#eaf1ff", color: "#2563eb", borderRadius: 20, padding: "3px 12px", fontSize: 14, fontWeight: 800 }}>{r.totalEntries}</span>
                                </td>
                                <td style={{ padding: "12px 14px" }}>
                                  <span style={{ color: "#475569", fontWeight: 600, fontSize: 14 }}>{r.filterStatus || "—"}</span>
                                </td>
                                <td style={{ padding: "12px 14px", color: "#64748b", fontSize: 14, fontWeight: 500, lineHeight: 1.4 }}>
                                  {r.submittedAt?.toDate ? (
                                    <>
                                      <div style={{ color: "#1e293b", fontWeight: 700 }}>{new Date(r.submittedAt.toDate()).toLocaleDateString("en-IN", { day: '2-digit', month: 'short', year: 'numeric' })}</div>
                                      <div style={{ fontSize: 13, color: "#94a3b8" }}>{new Date(r.submittedAt.toDate()).toLocaleTimeString("en-IN", { hour: '2-digit', minute: '2-digit', hour12: true })}</div>
                                    </>
                                  ) : "—"}
                                </td>
                                <td style={{ padding: "12px 14px" }}>
                                  <button 
                                    onClick={() => handleDeleteSubmission(r.id)}
                                    title="Delete Submission Record"
                                    style={{ background: "#fef2f2", color: "#dc2626", border: "1px solid #fee2e2", borderRadius: 8, padding: "8px", cursor: "pointer", transition: "all 0.2s", display: "flex", alignItems: "center", justifyContent: "center" }}
                                    onMouseEnter={e => { e.currentTarget.style.background = "#fee2e2"; }}
                                    onMouseLeave={e => { e.currentTarget.style.background = "#fef2f2"; }}
                                  >
                                    🗑️
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  );
                })()}
              </div>
            </>
          ) : (
            <>
              {/* Filters */}
              <div style={{ background: "#fff", borderRadius: 16, padding: "18px 20px", boxShadow: "0 2px 10px rgba(15,37,87,0.07)", marginBottom: 20, display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 14 }}>
                <div>
                  <label style={{ fontSize: 13, fontWeight: 700, color: "#374151", display: "block", marginBottom: 5 }}>🏢 Department</label>
                  <select value={filterDept} onChange={e => setFilterDept(e.target.value)}
                    style={{ width: "100%", background: "#f8fafc", border: "1.5px solid #e2e8f0", borderRadius: 8, padding: "8px 12px", fontSize: 15, outline: "none", cursor: "pointer" }}>
                    <option value="">All Departments</option>
                    {departments.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: 13, fontWeight: 700, color: "#374151", display: "block", marginBottom: 5 }}>🪪 Employee ID</label>
                  <input value={filterEmpId} onChange={e => setFilterEmpId(e.target.value)} placeholder="Search emp ID..."
                    style={{ width: "100%", background: "#f8fafc", border: "1.5px solid #e2e8f0", borderRadius: 8, padding: "8px 12px", fontSize: 15, outline: "none", boxSizing: "border-box" }} />
                </div>
                <div>
                  <label style={{ fontSize: 13, fontWeight: 700, color: "#374151", display: "block", marginBottom: 5 }}>👤 Faculty Name</label>
                  <input value={filterName} onChange={e => setFilterName(e.target.value)} placeholder="Search name..."
                    style={{ width: "100%", background: "#f8fafc", border: "1.5px solid #e2e8f0", borderRadius: 8, padding: "8px 12px", fontSize: 15, outline: "none", boxSizing: "border-box" }} />
                </div>
                <div>
                  <label style={{ fontSize: 13, fontWeight: 700, color: "#374151", display: "block", marginBottom: 5 }}>📌 Status</label>
                  <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
                    style={{ width: "100%", background: "#f8fafc", border: "1.5px solid #e2e8f0", borderRadius: 8, padding: "8px 12px", fontSize: 15, outline: "none", cursor: "pointer" }}>
                    <option value="">All Status / DOI</option>
                    {statusOptions.map(s => <option key={s} value={s}>{s}</option>)}
                    <option value="true">Verified (DOI)</option>
                    <option value="false">Not Verified (DOI)</option>
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: 13, fontWeight: 700, color: "#374151", display: "block", marginBottom: 5 }}>📅 From Date</label>
                  <input type="date" value={filterDateFrom} onChange={e => setFilterDateFrom(e.target.value)}
                    style={{ width: "100%", background: "#f8fafc", border: "1.5px solid #e2e8f0", borderRadius: 8, padding: "8px 12px", fontSize: 15, outline: "none", boxSizing: "border-box", colorScheme: "light" }} />
                </div>
                <div>
                  <label style={{ fontSize: 13, fontWeight: 700, color: "#374151", display: "block", marginBottom: 5 }}>📅 To Date</label>
                  <input type="date" value={filterDateTo} onChange={e => setFilterDateTo(e.target.value)}
                    style={{ width: "100%", background: "#f8fafc", border: "1.5px solid #e2e8f0", borderRadius: 8, padding: "8px 12px", fontSize: 15, outline: "none", boxSizing: "border-box", colorScheme: "light" }} />
                </div>
                <div>
                  <label style={{ fontSize: 13, fontWeight: 700, color: "#374151", display: "block", marginBottom: 5 }}>📤 Submission</label>
                  <select value={filterSubmission} onChange={e => setFilterSubmission(e.target.value)}
                    style={{ width: "100%", background: filterSubmission === "submitted" ? "#f0fdf4" : filterSubmission === "not_submitted" ? "#fff1f2" : "#f8fafc", border: filterSubmission === "submitted" ? "1.5px solid #86efac" : filterSubmission === "not_submitted" ? "1.5px solid #fecaca" : "1.5px solid #e2e8f0", borderRadius: 8, padding: "8px 12px", fontSize: 15, outline: "none", cursor: "pointer", fontWeight: filterSubmission ? 700 : 400 }}>
                    <option value="">All Faculty</option>
                    <option value="submitted">✅ Submitted</option>
                    <option value="not_submitted">⚠ Not Submitted</option>
                  </select>
                </div>
                <div style={{ display: "flex", alignItems: "flex-end" }}>
                  <button onClick={() => { setFilterDept(""); setFilterEmpId(""); setFilterName(""); setFilterStatus(""); setFilterSubmission(""); setFilterDateFrom(""); setFilterDateTo(""); }}
                    style={{ width: "100%", background: "#f1f5f9", color: "#475569", border: "1.5px solid #e2e8f0", borderRadius: 8, padding: "8px 12px", fontSize: 15, fontWeight: 700, cursor: "pointer" }}>
                    🔄 Reset
                  </button>
                </div>
              </div>

              {/* Report Table */}
              <div ref={printRef} style={{ background: "#fff", borderRadius: 16, boxShadow: "0 2px 10px rgba(15,37,87,0.07)", overflow: "hidden" }}>
                <div style={{ padding: "20px 24px", display: "none" }} className="print-only">
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 20, borderBottom: "2px solid #0f2557", paddingBottom: 16, marginBottom: 16 }}>
                    <img src="/apdch_logo.png" alt="APDCH" style={{ height: 80, width: 80, objectFit: "contain" }} />
                    <div style={{ textAlign: "center" }}>
                      <h1 style={{ color: "#0f2557", margin: 0, fontSize: 26, fontWeight: 900 }}>ADHIPARASAKTHI DENTAL COLLEGE & HOSPITAL</h1>
                      <div style={{ fontSize: 14, color: "#64748b", fontWeight: 700, marginTop: 4 }}>MELMARUVATHUR, TAMIL NADU - 603319</div>
                    </div>
                  </div>
                  
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 16 }}>
                    <div>
                      <h2 style={{ color: "#2563eb", margin: 0, fontSize: 20, fontWeight: 800 }}>{activeSec?.label} Consolidated Report</h2>
                      <div style={{ fontSize: 14, color: "#475569", marginTop: 4 }}>
                        {filterDept ? `Department: ${filterDept} | ` : ""}
                        {filterDateFrom || filterDateTo ? `Period: ${filterDateFrom || "Start"} to ${filterDateTo || "End"} | ` : ""}
                        Count: {reportRows.length} entries | Generated By: {user?.name || "Admin"}
                      </div>
                    </div>
                    <div style={{ textAlign: "right", fontSize: 13, color: "#94a3b8" }}>
                      <div>Printed: {new Date().toLocaleString()}</div>
                      <div style={{ fontWeight: 800, color: "#475569" }}>Consolidated Report v1.0</div>
                    </div>
                  </div>
                </div>

                {loading ? (
                  <div style={{ padding: 60, textAlign: "center", color: "#94a3b8" }}>⏳ Loading report...</div>
                ) : reportRows.length === 0 ? (
                  <div style={{ padding: 60, textAlign: "center", color: "#94a3b8" }}>
                    <div style={{ fontSize: 42, marginBottom: 12 }}>{activeSec?.icon}</div>
                    <p>No entries found for selected filters.</p>
                  </div>
                ) : (
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
                      <thead>
                        <tr style={{ background: activeSec?.light || "#f8fafc" }}>
                          {dynamicKeys.map((key, i) => selectedFields[key] && (
                            <th key={key} style={{ ...thS, color: activeSec?.color }}>
                              {key === "faculty_name" ? "Faculty Name" : dynamicCols[i]}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {reportRows.map(({ siNo, faculty, entry }, i) => (
                          <tr key={i} style={{ borderBottom: "1px solid #f1f5f9" }}
                            onMouseEnter={e => e.currentTarget.style.background = activeSec?.light || "#f8fafc"}
                            onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                            {dynamicKeys.map((key, ki) => selectedFields[key] && (
                              <td key={ki} style={{ ...tdS, fontWeight: key === "faculty_name" ? 600 : 400, color: key === "faculty_name" ? "#1e293b" : "#475569" }}>
                                 {key === "si_no" ? siNo : 
                                  key === "faculty_name" ? getNameWithTitle(faculty) :
                                  (() => {
                                    const val = getEntryValue(entry, key);
                                    if (typeof val === "string" && val.startsWith("http") && val.includes("firebasestorage")) {
                                      return <a href={val} target="_blank" rel="noreferrer" style={{ color: "#2563eb", fontWeight: 700, textDecoration: "none" }}>📎 View File</a>;
                                    }
                                    return val;
                                  })()}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </main>
      </div>

      <style>{`
        @media print {
          header, aside, .no-print { display: none !important; }
          .print-only { display: block !important; }
          body { background: white; }
          table { font-size: 10px; }
          td, th { padding: 6px 8px !important; }
        }
      `}</style>
    </div>
  );
}

const thS = { padding: "11px 12px", textAlign: "left", fontSize: 13, fontWeight: 700, letterSpacing: 0.5, borderBottom: "2px solid #e2e8f0", whiteSpace: "nowrap" };
const tdS = { padding: "10px 12px", fontSize: 14, color: "#475569" };
